import Dialog from "../../../General/ConfirmableDialog";
import React, { useState, ChangeEvent } from "react";
import {
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  FormControl,
  Box,
  CircularProgress,
} from "@mui/material";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";

interface IFileUploadModalProps {
  open: boolean;
  onClose: () => void;
  accident: any;
}

// 1 GB per-file size limit
const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1,073,741,824 bytes

const allowedExtensions = new Set(
  [
    ".jpg",
    ".jpeg",
    ".png",
    ".pdf",
    ".mp4",
    ".mov",
    ".avi",
    ".wmv",
    ".mkv",
  ].map((ext) => ext.toLowerCase())
);

const FileUploadModal: React.FC<IFileUploadModalProps> = ({
  open,
  onClose,
  accident,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const handleSnackbarClose = () => setStatus("idle");

const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    setErrorMessage(null);
    setStatus("idle");

    const selectedFiles = Array.from(event.target.files);
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    const oversizeFiles: string[] = [];

    selectedFiles.forEach((file) => {
      const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
      const isAllowed = allowedExtensions.has(extension);
      const isOversize = file.size > MAX_FILE_SIZE_BYTES;

      if (!isAllowed) {
        invalidFiles.push(file.name);
        return;
      }

      if (isOversize) {
        oversizeFiles.push(file.name);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length || oversizeFiles.length) {
      setStatus("error");
      const messages: string[] = [];
      if (invalidFiles.length) {
        messages.push(
          `Unsupported file type: ${invalidFiles.join(", ")}. Allowed extensions are ${[...allowedExtensions].join(", ")}.`
        );
      }
      if (oversizeFiles.length) {
        messages.push(
          `File size exceeds 1 GB limit: ${oversizeFiles.join(", ")}.`
        );
      }
      setErrorMessage(messages.join(" \u2014 "));
    }

    if (validFiles.length) {
      setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }

    event.target.value = "";
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!files.length || !accident) {
        throw new Error("No files selected or accident data is missing");
      }
      setLoading(true);

      // Double-check size on submit to be safe
      const oversizedOnSubmit = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES).map((f) => f.name);
      if (oversizedOnSubmit.length) {
        throw new Error(`File size exceeds 1 GB limit: ${oversizedOnSubmit.join(", ")}.`);
      }

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await axios.post(
        `${apiVariable}/accident/uploadMultiple/${accident.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setStatus("success");
      setLoading(false);
      setFiles([]);
      queryClient.invalidateQueries({
        queryKey: ["accidentImages", accident.id],
      });
      onClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setLoading(false);
      setErrorMessage(
        error?.response?.data || "Error occurred during file upload."
      );
    },
  });

  const handleSubmit = () => {
    uploadMutation.mutate();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Upload Files for Accident #{accident?.id}</DialogTitle>
        <DialogContent>
          <Box sx={{ marginBottom: 2 }}>
            <FormControl fullWidth>
              <Button
                variant="outlined"
                component="label"
                sx={{ marginBottom: 2 }}
              >
                Select Files
                <input
                  hidden
                  multiple
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.mp4,.mov,.avi,.wmv,.mkv"
                  onChange={handleFilesChange}
                />
              </Button>
              <Box sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                    Accepted formats: .jpg, .jpeg, .png, .pdf, .mp4, .mov, .avi, .wmv, .mkv. Max size per file: 1 GB.
              </Box>
                  {files.map((file, index) => (
                    <div key={index}>
                      {file.name}
                    </div>
                  ))}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
                disabled={loading || files.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      <StatusSnackbar
        status={status}
        successMessage="Files uploaded successfully!"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default FileUploadModal;
